import React, { useState, useEffect, useCallback, useRef } from 'react';
import HouseSelector from './components/HouseSelector';
import Dashboard from './components/Dashboard';
import CategoryTabs from './components/CategoryTabs';
import BudgetTab from './components/BudgetTab';
import ProjectsTab from './components/ProjectsTab';
import TripsTab from './components/TripsTab';
import ConnectionStatus from './components/ConnectionStatus';
import SettingsMenu from './components/SettingsMenu';
import InstallPrompt from './components/InstallPrompt';
import NotificationPrompt from './components/NotificationPrompt';
import Login from './components/Login';
import { authAPI, housesAPI, notificationsAPI } from './services/api';
import './App.css';

function App() {
  const [selectedHouseId, setSelectedHouseId] = useState(() => {
    try {
      const saved = localStorage.getItem('selectedHouseId');
      if (saved) {
        const id = parseInt(saved, 10);
        if (!isNaN(id)) return id;
      }
    } catch (e) {}
    return null;
  });

  // Stable callback - prevents infinite loop in HouseSelector's onToggleRef effect
  const handleHouseSelect = useCallback((houseId) => {
    setSelectedHouseId(houseId);
    if (houseId !== null) {
      localStorage.setItem('selectedHouseId', houseId.toString());
    } else {
      localStorage.removeItem('selectedHouseId');
    }
  }, []);
  const [activeView, setActiveView] = useState('dashboard');
  const [inventoryInitialTab, setInventoryInitialTab] = useState(null);
  const [projectsInitialId, setProjectsInitialId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const houseToggleRef = useRef(null);
  const setHouseToggleRef = useCallback((fn) => {
    houseToggleRef.current = fn;
  }, []);
  const [houses, setHouses] = useState([]);
  const [notificationEnabled, setNotificationEnabled] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setIsAuthenticated(false);
        setIsCheckingAuth(false);
        return;
      }

      try {
        const response = await authAPI.getMe();
        setCurrentUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        // Token invalid or expired
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_user_data');
        setIsAuthenticated(false);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, []);

  // Restore selected house from localStorage when logging back in (after logout)
  useEffect(() => {
    if (isAuthenticated && selectedHouseId === null) {
      const savedHouseId = localStorage.getItem('selectedHouseId');
      if (savedHouseId) {
        const houseId = parseInt(savedHouseId, 10);
        if (!isNaN(houseId)) {
          setSelectedHouseId(houseId);
        }
      }
    }
  }, [isAuthenticated, selectedHouseId]);

  // Load houses list and validate selected house exists (only clear if deleted)
  useEffect(() => {
    if (isAuthenticated) {
      const loadHouses = async () => {
        try {
          const response = await housesAPI.getAll();
          const housesData = Array.isArray(response.data) ? response.data : [];
          housesData.sort((a, b) => (a.id || 0) - (b.id || 0));
          setHouses(housesData);
          // Only clear if selected house was deleted - never overwrite with a different house
          setSelectedHouseId((current) => {
            if (!current) return current;
            if (housesData.length === 0 || !housesData.some((h) => h.id === current)) {
              localStorage.removeItem('selectedHouseId');
              return null;
            }
            return current;
          });
        } catch (error) {
          console.error('Error loading houses:', error);
        }
      };
      loadHouses();
    }
  }, [isAuthenticated]);

  // Check notification status after login (independent of house selection)
  useEffect(() => {
    if (!isAuthenticated) return;
    const checkNotificationStatus = async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setNotificationEnabled(!!subscription);
        } catch (error) {
          console.error('Error checking notification status:', error);
        }
      }
    };
    checkNotificationStatus();
  }, [isAuthenticated]);

  // Register service worker; check for updates so iOS/PWA picks up new deploys
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let refreshing = false;
    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    let registration;
    const onVisible = () => {
      if (document.visibilityState === 'visible' && registration) {
        registration.update().catch(() => {});
      }
    };

    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        registration = reg;
        reg.update().catch(() => {});
        document.addEventListener('visibilitychange', onVisible);
      })
      .catch((error) => {
        console.error('Service Worker registration failed:', error);
      });

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const handleLogin = async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const response = await authAPI.getMe();
        setCurrentUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_user_data');
    localStorage.removeItem('selectedHouseId');
    setIsAuthenticated(false);
    setCurrentUser(null);
    setSelectedHouseId(null);
    setActiveView('dashboard');
  };

  const handleHouseToggle = useCallback(() => {
    if (houseToggleRef.current) {
      houseToggleRef.current();
    } else if (houses.length > 1) {
      // Fallback: directly toggle if ref not available
      const currentIndex = houses.findIndex(h => h.id === selectedHouseId);
      const nextIndex = (currentIndex + 1) % houses.length;
      const nextHouse = houses[nextIndex];
      if (nextHouse) {
        localStorage.setItem('selectedHouseId', nextHouse.id.toString());
        setSelectedHouseId(nextHouse.id);
      }
    }
  }, [houses, selectedHouseId]);

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const enablePushNotifications = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      throw new Error('Notifications are not supported in this browser');
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }
    }

    if (Notification.permission !== 'granted') {
      throw new Error('Notification permission denied');
    }

    const registration = await navigator.serviceWorker.ready;
    const keyResponse = await notificationsAPI.getVapidPublicKey();
    const vapidPublicKey = keyResponse.data.publicKey;
    if (!vapidPublicKey) {
      throw new Error('Push notifications are not configured on the server');
    }

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });
    }

    await notificationsAPI.subscribe({
      endpoint: subscription.endpoint,
      p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
      auth: arrayBufferToBase64(subscription.getKey('auth')),
      house_id: null,
    });
    setNotificationEnabled(true);
  };

  const handleNotificationToggle = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      alert('Notifications are not supported in this browser');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        await notificationsAPI.unsubscribeByEndpoint(subscription.endpoint);
        setNotificationEnabled(false);
      } else {
        await enablePushNotifications();
      }
    } catch (error) {
      console.error('Error toggling notifications:', error);
      alert('Failed to toggle notifications: ' + error.message);
    }
  };

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="App">
        <div className="auth-loading">
          <div className="loading-spinner-large"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-left">
          <div className="logo-container">
            <img 
              src="/logo.svg" 
              alt="HomeManager Logo" 
              className="app-logo"
            />
            <h1>HomeManager</h1>
            <div style={{ display: 'none' }}>
              <HouseSelector
                selectedHouseId={selectedHouseId}
                onHouseChange={handleHouseSelect}
                isHeader={true}
                onToggleRef={setHouseToggleRef}
              />
            </div>
          </div>
        </div>
        <div className="header-right">
          <nav className="app-nav">
            <button
              className={activeView === 'dashboard' ? 'active' : ''}
              onClick={() => setActiveView('dashboard')}
            >
              <span>📊</span>
              <span>Dashboard</span>
            </button>
            <button
              className={activeView === 'inventory' ? 'active' : ''}
              onClick={() => setActiveView('inventory')}
            >
              <span>📦</span>
              <span>Inventory</span>
            </button>
            <button
              className={activeView === 'budget' ? 'active' : ''}
              onClick={() => setActiveView('budget')}
            >
              <span>💰</span>
              <span>Budget</span>
            </button>
            <button
              className={activeView === 'projects' ? 'active' : ''}
              onClick={() => setActiveView('projects')}
            >
              <span>🏗️</span>
              <span>Projects</span>
            </button>
            <button
              className={activeView === 'trips' ? 'active' : ''}
              onClick={() => setActiveView('trips')}
            >
              <span>🧳</span>
              <span>Trips</span>
            </button>
          </nav>
          <div className="user-menu">
            <span className="user-name">{currentUser?.username || localStorage.getItem('auth_user')}</span>
          </div>
          <SettingsMenu
            selectedHouseId={selectedHouseId}
            houses={houses}
            onHouseToggle={handleHouseToggle}
            onLogout={handleLogout}
            onNotificationToggle={handleNotificationToggle}
            notificationEnabled={notificationEnabled}
          />
        </div>
      </header>

      <main className="app-main">
        <ConnectionStatus />
        <InstallPrompt />
        <NotificationPrompt
          enabled={notificationEnabled}
          onEnable={async () => {
            try {
              await enablePushNotifications();
            } catch (error) {
              alert(error.message || 'Failed to enable notifications');
              throw error;
            }
          }}
        />

        {activeView === 'dashboard' && (
          <Dashboard 
            selectedHouseId={selectedHouseId}
            onHouseSelect={handleHouseSelect}
            onNavigate={(view, tab, projectId) => {
              if (tab) setInventoryInitialTab(tab);
              if (projectId) setProjectsInitialId(projectId);
              setActiveView(view);
            }} 
          />
        )}
        {activeView === 'inventory' && (
          <CategoryTabs 
            houseId={selectedHouseId} 
            initialTab={inventoryInitialTab}
            onTabChange={() => setInventoryInitialTab(null)}
          />
        )}
        {activeView === 'budget' && <BudgetTab houseId={selectedHouseId} />}
        {activeView === 'projects' && (
          <ProjectsTab
            houseId={selectedHouseId}
            initialProjectId={projectsInitialId}
            onProjectViewed={() => setProjectsInitialId(null)}
          />
        )}
        {activeView === 'trips' && <TripsTab />}
      </main>
    </div>
  );
}

export default App;

